import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PartyPopperIcon,
  TagIcon,
  BarChart3Icon,
  FilesIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XIcon,
} from 'lucide-react';
import { Button } from './ui/button';

// Celebration + onboarding tutorial shown ONLY after a Solo user creates their
// very first expense. Backend returns is_first_expense=true → SoloExpenseEntry
// renders this modal instead of navigating immediately. Users can skip at any
// step; final step navigates to /dashboard/expenses.
const TUTORIAL_STEPS = [
  {
    Icon: TagIcon,
    color: 'emerald',
    title: 'Kategorilerle düzen kur',
    body:
      'Harcamalarına kategori atadığında ay sonunda nereye ne kadar harcadığın anında ortaya çıkar. ' +
      'Masraf Merkezleri menüsünden istediğin kadar kategori ve proje ekleyebilirsin.',
  },
  {
    Icon: BarChart3Icon,
    color: 'blue',
    title: 'Dashboard ile takip et',
    body:
      'Ana sayfadaki Solo özetinde — Şirket vs Kişisel, bekleyen geri ödeme, aylık trend — her şey ' +
      'tek bakışta. Para birimi filtrelerini değiştirerek farklı kırılımları görebilirsin.',
  },
  {
    Icon: FilesIcon,
    color: 'purple',
    title: 'Belgeleri bir arada tut',
    body:
      'Her harcamaya fiş veya fatura ekleyebilirsin (PDF/JPG/PNG · maks 10MB). Tüm dosyaların ' +
      'Belgeler menüsünde aranabilir şekilde tek listede toplanır — vergi dönemi geldiğinde ' +
      'aramak yok, hepsi orada.',
  },
];

const SoloFirstExpenseCelebration = ({ open, onClose, onFinish }) => {
  const [stepIdx, setStepIdx] = useState(0);

  if (!open) return null;

  const step = TUTORIAL_STEPS[stepIdx];
  const isLast = stepIdx === TUTORIAL_STEPS.length - 1;
  const Icon = step.Icon;
  const c = step.color;

  // Tailwind safelist — kept inline so JIT picks them up.
  const colorMap = {
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', btn: 'bg-emerald-500 hover:bg-emerald-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', btn: 'bg-blue-500 hover:bg-blue-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', btn: 'bg-purple-500 hover:bg-purple-600' },
  };
  const cc = colorMap[c];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="solo-celebration-modal"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={stepIdx}
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.25 }}
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8"
        >
          {/* Close (skip) */}
          <button
            type="button"
            onClick={onFinish}
            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Kapat"
            data-testid="solo-celebration-skip"
          >
            <XIcon className="w-5 h-5" />
          </button>

          {/* Header: only on first step */}
          {stepIdx === 0 && (
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-3"
              >
                <PartyPopperIcon className="w-9 h-9 text-amber-500" />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900">
                Tebrikler! 🎉
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                İlk harcamanızı başarıyla eklediniz. Solo akışınızdan en iyi şekilde
                faydalanmak için 30 saniyelik bir tura ne dersin?
              </p>
            </div>
          )}

          {/* Tutorial step */}
          <div className="flex items-start gap-4 my-4" data-testid={`solo-celebration-step-${stepIdx}`}>
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${cc.bg}`}>
              <Icon className={`w-6 h-6 ${cc.text}`} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-6 mb-5">
            {TUTORIAL_STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIdx ? 'w-6 bg-gray-700' : 'w-1.5 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between gap-3">
            {stepIdx > 0 ? (
              <button
                type="button"
                onClick={() => setStepIdx(stepIdx - 1)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                data-testid="solo-celebration-back"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Geri
              </button>
            ) : (
              <button
                type="button"
                onClick={onFinish}
                className="text-sm text-gray-400 hover:text-gray-600"
                data-testid="solo-celebration-dismiss"
              >
                Şimdi atla
              </button>
            )}
            {isLast ? (
              <Button
                onClick={onFinish}
                className={`${cc.btn} text-white`}
                data-testid="solo-celebration-finish"
              >
                Harcamalarımı gör
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setStepIdx(stepIdx + 1)}
                className={`${cc.btn} text-white`}
                data-testid="solo-celebration-next"
              >
                İleri
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SoloFirstExpenseCelebration;
